import { useCallback, useMemo, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import {
  AssumptionDefinition,
  InputsSnapshot,
  OutputDefinition,
  OutputsSnapshot,
  OutputItem,
} from "@/types/excel/types";
import { excelToUiNumber, uiToExcelNumber } from "@/lib/excel/format";

type Params = {
  mapping: AssumptionDefinition[];
  outputs: OutputDefinition[];
};

function safeSheet(workbook: ExcelJS.Workbook, sheetName: string) {
  try {
    const ws = workbook.getWorksheet(sheetName);
    return ws ?? null;
  } catch (error) {
    console.error(`Error getting worksheet ${sheetName}:`, error);
    return null;
  }
}

function excelToUiText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (value instanceof Date) return value.toISOString();

  if (typeof value === "object") {
    const v: any = value;
    if (typeof v?.text === "string") return v.text;
    if (v?.result !== undefined) {
      return String(v.result);
    }
    if (Array.isArray(v?.richText)) {
      return v.richText.map((p: any) => p?.text ?? "").join("");
    }
  }

  return String(value || "");
}

function getValueByPath(obj: any, path: string): any {
  if (!path || !obj) return undefined;

  try {
    return path.split(".").reduce((current, key) => {
      if (current === null || current === undefined) return undefined;

      if (Array.isArray(current) && /^\d+$/.test(key)) {
        const index = parseInt(key, 10);
        return index < current.length ? current[index] : undefined;
      }

      return current[key];
    }, obj);
  } catch (error) {
    console.error(`Error getting value by path ${path}:`, error);
    return undefined;
  }
}

function setValueByPath(obj: any, path: string, value: any): void {
  if (!obj || !path) return;

  try {
    const parts = path.split(".");
    const last = parts.pop()!;

    let current = obj;
    for (let i = 0; i < parts.length; i++) {
      const key = parts[i];

      if (current[key] === undefined || current[key] === null) {
        const nextKey = parts[i + 1];
        if (nextKey && /^\d+$/.test(nextKey)) {
          current[key] = [];
        } else {
          current[key] = {};
        }
      }

      current = current[key];
    }

    if (/^\d+$/.test(last) && Array.isArray(current)) {
      const index = parseInt(last, 10);
      current[index] = value;
    } else {
      current[last] = value;
    }
  } catch (error) {
    console.error(`Error setting value by path ${path}:`, error);
  }
}

function formatOutputValue(value: any, format: string): any {
  if (value === null || value === undefined) return null;

  if (format === "percent" && typeof value === "number") {
    return value * 100;
  }

  if (format === "currency" && typeof value === "number") {
    return value;
  }

  if (typeof value === "object") {
    if (value?.result !== undefined) return value.result;
    if (value?.text !== undefined) return value.text;
    if (Array.isArray(value?.richText)) {
      return value.richText.map((p: any) => p?.text ?? "").join("");
    }
  }

  return value;
}

export function useDealExcelWorkbook({ mapping, outputs }: Params) {
  const workbookRef = useRef<ExcelJS.Workbook | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [dealData, setDealData] = useState<any>(null);
  const [lastInputs, setLastInputs] = useState<InputsSnapshot>({});

  const loadFromFile = useCallback(
    async (file: File) => {
      try {
        const buf = await file.arrayBuffer();
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(buf);

        workbookRef.current = wb;
        setFileName(file.name);
        setIsLoaded(true);

        const snapshot: InputsSnapshot = {};
        for (const def of mapping) {
          try {
            const ws = safeSheet(wb, def.sheet);
            if (!ws) continue;

            const cell = ws.getCell(def.cell);
            if (!cell) continue;

            if (def.format === "text") {
              snapshot[def.key] = excelToUiText(cell.value);
              continue;
            }

            const n = excelToUiNumber(cell.value, def.format);
            if (typeof n === "number") {
              snapshot[def.key] = n;
            }
          } catch (error) {
            console.error(
              `Error reading cell ${def.sheet}!${def.cell}:`,
              error,
            );
          }
        }
        setLastInputs(snapshot);
      } catch (error) {
        console.error("Error loading file:", error);
        throw error;
      }
    },
    [mapping],
  );

  const loadFromDeal = useCallback(
    async (deal: any, file?: File) => {
      try {
        if (file) {
          await loadFromFile(file);
        }

        setDealData(deal);

        const snapshot: InputsSnapshot = {};
        for (const def of mapping) {
          try {
            const value = getValueByPath(deal, def.key);
            if (value !== undefined && value !== null) {
              snapshot[def.key] = value;
            }
          } catch (error) {
            console.error(`Error extracting value for ${def.key}:`, error);
          }
        }
        setLastInputs(snapshot);
      } catch (error) {
        console.error("Error loading from deal:", error);
        throw error;
      }
    },
    [mapping, loadFromFile],
  );

  const getInputsSnapshot = useCallback((): InputsSnapshot => {
    return { ...lastInputs };
  }, [lastInputs]);

  const setInputs = useCallback(
    async (next: InputsSnapshot) => {
      const wb = workbookRef.current;
      if (!wb) return;

      try {
        for (const def of mapping) {
          if (!(def.key in next)) continue;

          const ws = safeSheet(wb, def.sheet);
          if (!ws) continue;

          const value = next[def.key];

          if (def.format === "text") {
            ws.getCell(def.cell).value = String(value ?? "");
            continue;
          }

          if (value !== null && value !== undefined) {
            const numValue = typeof value === "number" ? value : Number(value);
            if (!isNaN(numValue)) {
              const excelValue = uiToExcelNumber(numValue, def.format);
              ws.getCell(def.cell).value = excelValue;
            }
          }
        }

        if (dealData) {
          const updatedDeal = JSON.parse(JSON.stringify(dealData)); 
          for (const [key, value] of Object.entries(next)) {
            if (value !== undefined) {
              setValueByPath(updatedDeal, key, value);
            }
          }
          setDealData(updatedDeal);
        }

        setLastInputs(next);
      } catch (error) {
        console.error("Error setting inputs:", error);
        throw error;
      }
    },
    [mapping, dealData],
  );

  const getOutputsSnapshot = useCallback((): OutputsSnapshot => {
    const wb = workbookRef.current;
    if (!wb) return [];

    const results: OutputsSnapshot = [];

    for (const o of outputs) {
      try {
        const ws = safeSheet(wb, o.sheet);
        if (!ws) {
          results.push({
            key: o.key,
            label: o.label,
            value: null,
            format: o.format,
          });
          continue;
        }

        const cell = ws.getCell(o.cell);
        const rawValue = cell ? cell.value : null;
        const formattedValue = formatOutputValue(rawValue, o.format);

        results.push({
          key: o.key,
          label: o.label,
          value: formattedValue,
          format: o.format,
        });
      } catch (error) {
        console.error(`Error reading output ${o.key}:`, error);
        results.push({
          key: o.key,
          label: o.label,
          value: null,
          format: o.format,
        });
      }
    }

    return results;
  }, [outputs]);

  const exportWorkbook = useCallback(async () => {
    const wb = workbookRef.current;
    if (!wb) return;

    try {
      const out = await wb.xlsx.writeBuffer();
      const blob = new Blob([out], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const safeName = fileName?.trim()
        ? fileName.replace(/\.xlsx$/i, "")
        : "model";
      const dealSuffix = dealData?.name
        ? `_${dealData.name.replace(/[^a-z0-9]/gi, "_")}`
        : "";
      saveAs(blob, `${safeName}${dealSuffix}_updated.xlsx`);
    } catch (error) {
      console.error("Error exporting workbook:", error);
      throw error;
    }
  }, [fileName, dealData]);

  const getWorkbook = useCallback(() => {
    return workbookRef.current;
  }, []);

  const getDealData = useCallback(() => {
    return dealData ? JSON.parse(JSON.stringify(dealData)) : null;
  }, [dealData]);

  const updateFromDeal = useCallback(
    (deal: any) => {
      setDealData(deal);

      const snapshot: InputsSnapshot = {};
      for (const def of mapping) {
        try {
          const value = getValueByPath(deal, def.key);
          if (value !== undefined && value !== null) {
            snapshot[def.key] = value;
          }
        } catch (error) {
          console.error(`Error updating from deal for ${def.key}:`, error);
        }
      }
      setLastInputs(snapshot);
    },
    [mapping],
  );

  return useMemo(
    () => ({
      fileName,
      isLoaded,
      dealData,
      loadFromFile,
      loadFromDeal,
      getInputsSnapshot,
      setInputs,
      getOutputsSnapshot,
      exportWorkbook,
      getWorkbook,
      getDealData,
      updateFromDeal,
    }),
    [
      fileName,
      isLoaded,
      dealData,
      loadFromFile,
      loadFromDeal,
      getInputsSnapshot,
      setInputs,
      getOutputsSnapshot,
      exportWorkbook,
      getWorkbook,
      getDealData,
      updateFromDeal,
    ],
  );
}
